<template>
<Transition name="banner">
    <div v-if="displayBanner" class="promo-sticky-container">
        <div class="promo-container">
            <Transition name="asset" appear>
                <div class="asset-container">
                    <NuxtImg
                        v-if="promoDetails?.asset?.data?.attributes?.url"
                        class="asset"
                        :src="`${promoDetails?.asset?.data?.attributes?.url}?format=auto&width=300`"
                        :alt="promoDetails?.asset?.data?.attributes?.alternativeText || ''"
                    />
                </div>
            </Transition>
            <ProductBanner
                v-if="promotion && promotion.productCta"
                :promotion="promotion.productCta"
                :shopify-product="product"
            />
            <InformationalBanner
                v-else-if="promotion && promotion.informationalCta"
                :promotion="promotion.informationalCta"
            />
        </div>
        <div
            role="button" tabIndex="0" aria-label="close product banner"
            class="close-button"
            v-on:click="close"
            v-on:keydown.enter="close"
        >
            <UiIcon
                icon="chevron-xmark"
                alt="chevron-xmark"
                icon-color="white"
            />
        </div>
    </div>
</Transition>
</template>

<script setup lang="ts">
import { Shopify } from '@jackboxgames/shopify'
import type { JBGWeb } from '$types/JBGWeb'
import ProductBanner from '$components/shopping/promotions/ProductBanner.vue'
import InformationalBanner from '$components/shopping/promotions/InformationalBanner.vue'
import { currentCountry, ShoppingCart } from '$services/shopify'
import UiIcon from '~/public/images/icons/ui/UiIcon.vue'

const app = useNuxtApp()
const bannerVisible = ref(false)
const closeBannerPressed = ref(false)
const route = useRoute()
const isPackDetailPage = computed(() => route.name?.toString().startsWith('games-packs-packslug'))
const isExternalGamePage = computed(() => route.name?.toString().startsWith('games-publishing-externalgameslug'))

const populate = [
    'productCta',
    'productCta.asset',
    'productCta.packDetail',
    'productCta.gameDetail',
    'productCta.bundleDetail',
    'informationalCta',
    'informationalCta.asset'
]

onMounted(() => {
    // delays the banner popping up by 1s for visual purposes
    setTimeout(() => {
        checkCart()
    }, 1000)
})

const { data: promotions } = await useAsyncData<JBGWeb.Promotion[]>(
    'promotions',
    async () => {
        try {
            return app.$cms.fetchMany<JBGWeb.Promotion[]>('promotions', {
                populate,
                filters: {
                    enabled: { $eq: true }
                },
                locale: app.$i18n.locale.value
            })
        } catch (err: any) {
            console.error('Failed to fetch promotions:', err.message)
            return null
        }
    }
)

// just use the first object for now, will change later if we want to rotate the promos
const promotion = computed(() => promotions.value?.[0])

const { data: product } = await useAsyncData<Shopify.Product | null>(
    'promotion-product',
    async () => {
        if (!promotion.value.productCta) return null
        const productCta = promotion.value.productCta
        const collectionName = getProductCollectionName(productCta)

        try {
            const pack = await app.$cms.fetchOne(collectionName, String(productCta.shopifySlug), {
                populate: ['shopifyProduct'],
                locale: app.$i18n.locale.value,
                countryCode: currentCountry.code
            })
            return pack.shopifyProduct || null
        } catch (err: any) {
            console.error('Failed to fetch promotion product:', err.message)
            return null
        }
    }
)

const promoDetails = computed(() => {
    const p = promotion.value
    if (!p) return null

    if (p.productCta) {
        return p.productCta
    }

    if (p.informationalCta) {
        return p.informationalCta
    }

    return null
})

const displayBanner = computed(() => {
    const id = promoDetails.value?.promoId
    if (!id) return false
    if (isBannerTimedOut(String(id))) return false

    return promotion.value && bannerVisible.value && !isPackDetailPage.value && !isExternalGamePage.value
})

watch(ShoppingCart, () => {
    checkCart()
})

function checkCart() {
    if (closeBannerPressed.value) {
        bannerVisible.value = false
        return
    }

    if (ShoppingCart.isOpen()) {
        bannerVisible.value = false
        return
    }

    if (promotion.value?.productCta) {
        const cartItems = ShoppingCart.getCart()
        const hasProduct = cartItems.some((item) => item.productId === product.value?.id)
        bannerVisible.value = !hasProduct
    } else {
        bannerVisible.value = true
    }
}

function isBannerTimedOut(promoId: string) {
    if (!import.meta.client) return false
    return document.cookie.includes(`${promoId}_banner_timeout`)
}

function setBannerTimeoutCookie(bannerName: string) {
    // don't show the banner again for 1 hour
    const expDate = new Date()
    expDate.setTime(expDate.getTime() + 1 * 60 * 60 * 1000)
    document.cookie = `name=${bannerName}_banner_timeout; expires=${expDate.toUTCString()}; Secure`
}

function close() {
    const id = promoDetails.value?.promoId
    setBannerTimeoutCookie(String(id))
    closeBannerPressed.value = true
    bannerVisible.value = false
}

function getProductCollectionName(productCta: ProductCta): string | null {
    if (productCta.packDetail?.data) {
        return 'pack-details'
    }
    if (productCta.gameDetail?.data) {
        return 'game-details'
    }
    if (productCta.bundleDetail?.data) {
        return 'bundle-details'
    }
    return null
}

</script>

<style lang="scss">
@use "$styles/kit.scss" as *;

$gradient-bg: linear-gradient(157deg, rgba(100, 25, 241, 0.10) 0%,
        rgba(233, 149, 22, 0.10) 43%,
        rgba(229, 77, 207, 0.10) 100%),
        rgba(0, 0, 0, 0.88);
$gradient-border: linear-gradient(157deg, #6419F1 0%, #E99516 43%, #E54DCF 100%);
$border-width: 2px;
$border-radius: 12px;

.promo-sticky-container {
    position: fixed;
    bottom: 0;
    right: 24px;
    width: 408px;
    height: auto;
    z-index: 1000000;
    backdrop-filter: blur(16px);
    border-top-left-radius: $border-radius;
    border-top-right-radius: $border-radius;

    @media screen and (min-width: 200px) and (max-width: 439px){
        width: calc(100vw - 50px);
        right: 0;
        left: 0;
        margin: 0 auto;
    }

    .close-button {
        position: absolute;
        top: -12px;
        right: -12px;
        cursor: pointer;

        svg {
            width: 28px;
            height: 28px;

            &:hover {
                color: #B2BBFF;
            }
        }

        @media screen and (min-width: 200px) and (max-width: 439px){
            svg {
                width: 24px;
                height: 24px;
            }
        }
    }

    .promo-container {
        position: relative;
        width: 100%;
        height: 100%;
        background: $gradient-bg;
        background-clip: padding-box;
        border-top-left-radius: $border-radius;
        border-top-right-radius: $border-radius;
        border-left: $border-width solid transparent;
        border-right: $border-width solid transparent;
        border-top: $border-width solid transparent;

        &:before {
            content: '';
            position: absolute;
            top: -$border-width;
            left: -$border-width;
            right: -$border-width;
            bottom: 0;
            z-index: -1;
            border-top-left-radius: $border-radius;
            border-top-right-radius: $border-radius;
            background: $gradient-border;
        }

        .asset-container {
            position: absolute;
            bottom: 0px;
            left: -16px;
            width: 148px;
            height: 232px;
            overflow: visible;
            display: flex;
            justify-content: center;
            align-items: flex-end;

            @media screen and (min-width: 350px) and (max-width: 439px){
                width: 120px;
                height: 188px;
            }

            @media screen and (min-width: 200px) and (max-width: 350px){
                width: 100px;
                height: 157px;
                left: -12px;
            }

            .asset {
                width: 100%;
                height: 100%;
                max-height: 232px;
                object-fit: contain;
                padding: 0px;
                position:relative;
            }
        }
    }
}

.banner-enter-active {
    transition: all 0.5s ease-out;
}

.banner-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.banner-enter-from,
.banner-leave-to {
    transform: translateY(100%);
    opacity: 0;
}

.banner-enter-to,
.banner-leave-from {
  transform: translateY(0);
  opacity: 1;
}

.asset-enter-active {
  transition: all 1s ease-out 0.2s
}

.asset-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.asset-enter-from,
.asset-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.asset-enter-to,
.asset-leave-from {
  transform: translateY(0);
  opacity: 1;
}

// ios mobile safari query to take new nav bar into account
@supports (-webkit-touch-callout: default) {
    @media (hover: none) and (pointer: coarse) and (max-device-width: 767px) {

        .promo-sticky-container {
            border-bottom-left-radius: $border-radius;
            border-bottom-right-radius: $border-radius;

            .promo-container {
                border-bottom-left-radius: $border-radius;
                border-bottom-right-radius: $border-radius;
                border-bottom: $border-width solid transparent;

                &::before {
                    border-bottom-left-radius: $border-radius;
                    border-bottom-right-radius: $border-radius;
                    bottom: -$border-width;
                }
            }
        }

    }
}

</style>
